/**
 * STEP — useFeature hook
 * Returns whether a module feature is enabled for:
 *   1. The tenant's subscription plan (plan must have it enabled), AND
 *   2. The current user's accessible_modules (null = all plan modules allowed).
 */
import { useAuthStore, type ModuleFeatures } from "@/store/authStore";

/**
 * Why a feature is (un)available:
 *  - "allowed"      → plan includes it AND the current user's role may access it.
 *  - "plan_locked"  → the tenant's subscription plan doesn't include it (paywall).
 *  - "restricted"   → the plan includes it, but the owner/manager has restricted it
 *                     from this user's role via accessible_modules (permission, NOT paywall).
 */
export type FeatureAccess = "allowed" | "plan_locked" | "restricted";

export function useFeatureAccess(feature: keyof ModuleFeatures): FeatureAccess {
  const tenant = useAuthStore((s) => s.tenant);
  const user   = useAuthStore((s) => s.user);

  // Plan-level check first: if the plan doesn't include it, it's a paywall.
  const planEnabled = tenant?.module_features?.[feature] === true;
  if (!planEnabled) return "plan_locked";

  // OWNER always has full access regardless of accessible_modules.
  if (!user || user.role === "OWNER") return "allowed";

  // Per-user check: null = all plan modules; array = explicit whitelist.
  const userModules = user.accessible_modules;
  if (userModules === null || userModules === undefined) return "allowed";
  return userModules.includes(feature as string) ? "allowed" : "restricted";
}

export function useFeature(feature: keyof ModuleFeatures): boolean {
  return useFeatureAccess(feature) === "allowed";
}

export function useFeatures(): ModuleFeatures {
  const tenant = useAuthStore((s) => s.tenant);
  const user   = useAuthStore((s) => s.user);

  if (!tenant?.module_features) return {};

  // For OWNER or null accessible_modules, return plan features as-is
  if (!user || user.role === "OWNER" || user.accessible_modules == null) {
    return tenant.module_features;
  }

  // Intersect plan features with per-user whitelist
  const result: ModuleFeatures = {};
  const allowed = user.accessible_modules;
  for (const [key, val] of Object.entries(tenant.module_features)) {
    (result as Record<string, boolean>)[key] = val === true && allowed.includes(key);
  }
  return result;
}
